import clsx from "clsx";
import { splitProps } from "solid-js";
import type { JSX } from "solid-js";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export type ButtonProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

const baseClasses =
  "inline-flex items-center justify-center gap-2 rounded-[10px] font-semibold tracking-[-0.005em] " +
  "transition duration-150 ease-out select-none active:scale-[0.985] " +
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100";

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-[var(--accent-alt)] text-[var(--accent-fg)] shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_1px_2px_rgba(11,18,32,0.12),0_8px_18px_-8px_var(--accent-glow)] " +
    "hover:bg-[var(--accent-alt-hover)] active:bg-[var(--accent-pressed)] focus-visible:outline-[var(--accent-alt)]",
  secondary:
    "bg-[var(--bg-surface)] text-[var(--fg-strong)] border border-[var(--border-strong)] shadow-[0_1px_2px_rgba(11,18,32,0.05)] " +
    "hover:bg-[var(--bg-elevated)] hover:border-[var(--fg-subtle)] focus-visible:outline-[var(--accent-alt)]",
  ghost:
    "bg-transparent text-[var(--fg-default)] hover:bg-[var(--bg-inset)] hover:text-[var(--fg-strong)] focus-visible:outline-[var(--accent-alt)]",
  danger:
    "bg-[var(--danger)] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] hover:brightness-95 focus-visible:outline-[var(--danger)]",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-[15px]",
};

export function Button(props: ButtonProps) {
  const [local, rest] = splitProps(props, ["variant", "size", "class"]);
  const variant = (): Variant => local.variant ?? "primary";
  const size = (): Size => local.size ?? "md";

  return (
    <button
      type={rest.type ?? "button"}
      class={clsx(baseClasses, variantClasses[variant()], sizeClasses[size()], local.class)}
      {...rest}
    />
  );
}
